import React, { forwardRef, useCallback, useMemo } from 'react';
import { stripTags, replaceSpecialChars } from 'Utils';
import PropTypes from 'prop-types';
import styles from './styles.scss';
import classnames from 'classnames/bind';

export const headlineLevels = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6'];

export const headlinePropTypes = {
  additionalProps: PropTypes.object,
  additionalAriaLabelText: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  forceUppercase: PropTypes.bool,
  level: PropTypes.string,
  levelVisually: PropTypes.oneOf(headlineLevels),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  shy: PropTypes.number,
};

const cx = classnames.bind(styles);

const insertEveryNth = (value, insert, nth) =>
  value
    .split('')
    .flatMap((elem, idx) => ((idx + 1) % nth === 0 ? [elem, insert] : elem))
    .join('');

const Typography = () => <span />;

Typography.Text = forwardRef(
  (
    {
      additionalAriaLabelText,
      additionalProps,
      ariaLabel,
      className,
      forceUppercase,
      levelVisually,
      tag: Tag,
      value,
      shy = 0,
    },
    ref
  ) => {
    const cleanupFootnoteInfo = useCallback((value) =>
      value.replaceAll(/(\(siehe Fu(?:ß|ss)note (\d+)\))\d+/gi, ' $1')
    );

    const getAdditionalAriaLabelText = useCallback(
      (additionalAriaLabelText) => {
        let additionalAriaLabelTextConcat = Array.isArray(additionalAriaLabelText)
          ? additionalAriaLabelText.join(' ')
          : additionalAriaLabelText;

        if (additionalAriaLabelTextConcat) {
          // consumption text processing example
          // 1. input contains tags and special chars (simple example)
          // "<p>Tribbles are the machines<sup>(siehe Fußnote 42)<span class=\"sr-only\">42<\/span><\/sup> of the terrifÿing collision course.</p>"
          // 2. strip tags and replace special chars
          // "<p>Tribbles are the machines(siehe Fußnote 42)42 of the terrifying collision course.</p>"
          // 3. cleanup footnote info: insert space and remove duplicate footnote indicator/number
          // "<p>Tribbles are the machines (siehe Fußnote 42) of the terrifying collision course.</p>"
          additionalAriaLabelTextConcat = cleanupFootnoteInfo(
            stripTags(replaceSpecialChars(additionalAriaLabelTextConcat))
          );

          return ' - ' + additionalAriaLabelTextConcat;
        }

        return '';
      },
      [additionalAriaLabelText]
    );

    const getAriaLabel = useCallback(
      (ariaLabel, additionalAriaLabelText, forceUppercase, value) => {
        const formattedAdditionalAriaLabelText =
          getAdditionalAriaLabelText(additionalAriaLabelText);

        if (ariaLabel) {
          return ariaLabel + formattedAdditionalAriaLabelText;
        }

        if (typeof value === 'object') {
          console.error('prop.value is a node! Therefore, prop.ariaLabel must be set manually.');
          return undefined;
        }

        const text = cleanupFootnoteInfo(stripTags(replaceSpecialChars(value).trim()));
        const isSpecialCase = text.toUpperCase().includes('MAZDA6E');
        if (text === text.toUpperCase() || isSpecialCase) {
          return text.toLowerCase() + formattedAdditionalAriaLabelText;
        }

        if (forceUppercase || formattedAdditionalAriaLabelText !== '') {
          return text + formattedAdditionalAriaLabelText;
        }

        return undefined;
      },
      [ariaLabel, additionalAriaLabelText, forceUppercase, value]
    );

    const shyValue = useMemo(
      () =>
        !value || typeof value !== 'string' || shy === 0
          ? value
          : insertEveryNth(value, '&shy;', shy),
      [value, shy]
    );

    if (!value) {
      return null;
    }

    const visualContent =
      typeof value === 'string'
        ? {
            dangerouslySetInnerHTML: { __html: shyValue },
          }
        : {
            children: value,
          };

    // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Reference/Roles/generic_role
    const tagIsGeneric = Tag.toLowerCase() === 'span' || Tag.toLowerCase() === 'div';

    return (
      <Tag
        ref={ref}
        aria-label={
          tagIsGeneric
            ? undefined
            : getAriaLabel(ariaLabel, additionalAriaLabelText, forceUppercase, value)
        }
        className={cx(
          className,
          styles.typography,
          forceUppercase ? styles.forceUppercase : styles.initialCase,
          levelVisually ? styles[`typographyHeadline${levelVisually}`] : undefined
        )}
        {...visualContent}
        {...additionalProps}
      />
    );
  }
);

Typography.Text.propTypes = { ...headlinePropTypes, tag: PropTypes.string.isRequired };

Typography.Headline = forwardRef((props, ref) => {
  const formattedProps = { ...props, tag: props.level };
  delete formattedProps.level;

  return <Typography.Text ref={ref} {...formattedProps} />;
});

Typography.Headline.propTypes = {
  ...headlinePropTypes,
  level: PropTypes.oneOf(headlineLevels).isRequired,
};

export default Typography;
